import React, { useCallback, useRef } from 'react'
import { Pressable, ScrollView, StyleSheet, View } from 'react-native'
import { DS } from '@/design-system'
import { LoungeAvatar } from '../../../components/LoungeAvatar'

export const TABLE_COLOR_PRESETS = [
  { label: 'Default', color: null as string | null },
  { label: 'Emerald', color: '#1B6B4A' },
  { label: 'Navy', color: '#1E3A5F' },
  { label: 'Royal', color: '#4A2070' },
  { label: 'Slate', color: '#3D4F5F' },
  { label: 'Charcoal', color: '#2E2E38' },
  { label: 'Teal', color: '#1A5C5A' },
  { label: 'Crimson', color: '#8B1A1A' }
]

export const DEFAULT_TABLE_COLOR = '#812A50'

const GRADIENT_PRESETS = [
  { label: 'Maroon', color1: '#6B1D2A', color2: '#E8A0B0' },
  { label: 'Ocean', color1: '#1A3B6B', color2: '#A78BFA' },
  { label: 'Sunset', color1: '#D97706', color2: '#FBBF24' },
  { label: 'Forest', color1: '#065F46', color2: '#6EE7B7' }
]

const SOLID_PRESETS = [
  { label: 'Maroon', color: '#6B1D2A' },
  { label: 'Blue', color: '#1E40AF' },
  { label: 'Pink', color: '#BE185D' },
  { label: 'Orange', color: '#C2410C' }
]

export type AvatarConfig =
  | { type: 'dicebear'; style: 'shapes' | 'rings' | 'thumbs' | 'funEmoji' }
  | { type: 'gradient'; color1: string; color2: string }
  | { type: 'solid'; color: string }

type AvatarOptionItem = {
  key: string
  label: string
  avatar: AvatarConfig
}

function buildAvatarOptions(): AvatarOptionItem[] {
  const dicebear: AvatarOptionItem[] = (['shapes', 'rings', 'thumbs', 'funEmoji'] as const).map((s) => ({
    key: `db-${s}`,
    label: s,
    avatar: { type: 'dicebear' as const, style: s }
  }))
  const gradients: AvatarOptionItem[] = GRADIENT_PRESETS.map((g) => ({
    key: `grad-${g.color1}`,
    label: g.label,
    avatar: { type: 'gradient' as const, color1: g.color1, color2: g.color2 }
  }))
  const solids: AvatarOptionItem[] = SOLID_PRESETS.map((s) => ({
    key: `solid-${s.color}`,
    label: s.label,
    avatar: { type: 'solid' as const, color: s.color }
  }))
  return [...dicebear, ...gradients, ...solids]
}

const ALL_AVATAR_OPTIONS = buildAvatarOptions()

export function isActiveAvatar(option: AvatarConfig, current: AvatarConfig | null | undefined): boolean {
  if (!current) return false
  if (option.type !== current.type) return false
  if (option.type === 'dicebear' && current.type === 'dicebear') return option.style === current.style
  if (option.type === 'gradient' && current.type === 'gradient')
    return option.color1 === current.color1 && option.color2 === current.color2
  if (option.type === 'solid' && current.type === 'solid') return option.color === current.color
  return false
}

function AvatarOptionButton({
  children,
  active,
  label,
  onPress
}: {
  children: React.ReactNode
  active: boolean
  label: string
  onPress: () => void
}) {
  return (
    <Pressable onPress={onPress} style={styles.avatarOption}>
      <View style={[styles.avatarOptionRing, active && styles.avatarOptionRingActive]}>{children}</View>
      <DS.Text
        variant="Mini/Regular"
        style={{ color: DS.palette['Brand/Dark Grey'], textAlign: 'center' }}
        numberOfLines={1}
      >
        {label}
      </DS.Text>
    </Pressable>
  )
}

export function LoungeAvatarPicker({
  loungeName,
  currentAvatar,
  onSelect
}: {
  loungeName: string
  currentAvatar: AvatarConfig | null | undefined
  onSelect: (avatar: AvatarConfig) => void
}) {
  const scrollRef = useRef<ScrollView>(null)

  const sorted = [...ALL_AVATAR_OPTIONS].sort((a, b) => {
    const aActive = isActiveAvatar(a.avatar, currentAvatar) ? 0 : 1
    const bActive = isActiveAvatar(b.avatar, currentAvatar) ? 0 : 1
    return aActive - bActive
  })

  const handleSelect = useCallback(
    (avatar: AvatarConfig) => {
      onSelect(avatar)
      scrollRef.current?.scrollTo({ x: 0, animated: true })
    },
    [onSelect]
  )

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.row}
    >
      {sorted.map((opt) => (
        <AvatarOptionButton
          key={opt.key}
          label={opt.label}
          active={isActiveAvatar(opt.avatar, currentAvatar)}
          onPress={() => handleSelect(opt.avatar)}
        >
          <LoungeAvatar name={loungeName || 'Lounge'} branding={{ avatar: opt.avatar }} size={48} />
        </AvatarOptionButton>
      ))}
    </ScrollView>
  )
}

export function LoungeTableColorPicker({
  currentColor,
  onSelect
}: {
  currentColor: string | null | undefined
  onSelect: (color: string | null) => void
}) {
  const scrollRef = useRef<ScrollView>(null)
  const normalizedCurrent = currentColor ?? null

  const sorted = [...TABLE_COLOR_PRESETS].sort((a, b) => {
    const aActive = a.color === normalizedCurrent ? 0 : 1
    const bActive = b.color === normalizedCurrent ? 0 : 1
    return aActive - bActive
  })

  return (
    <ScrollView
      ref={scrollRef}
      horizontal
      showsHorizontalScrollIndicator={false}
      contentContainerStyle={styles.row}
    >
      {sorted.map((preset) => {
        const isActive = preset.color === normalizedCurrent
        const displayColor = preset.color ?? DEFAULT_TABLE_COLOR
        return (
          <Pressable
            key={preset.label}
            onPress={() => {
              onSelect(preset.color)
              scrollRef.current?.scrollTo({ x: 0, animated: true })
            }}
            style={styles.avatarOption}
          >
            <View style={[styles.avatarOptionRing, isActive && styles.avatarOptionRingActive]}>
              <View style={{ width: 48, height: 48, borderRadius: 24, backgroundColor: displayColor }} />
            </View>
            <DS.Text
              variant="Mini/Regular"
              style={{ color: DS.palette['Brand/Dark Grey'], textAlign: 'center' }}
              numberOfLines={1}
            >
              {preset.label}
            </DS.Text>
          </Pressable>
        )
      })}
    </ScrollView>
  )
}

const styles = StyleSheet.create({
  row: {
    gap: 12,
    paddingVertical: 4
  },
  avatarOption: {
    alignItems: 'center',
    gap: 4,
    width: 60
  },
  avatarOptionRing: {
    borderRadius: 26,
    borderWidth: 2,
    borderColor: 'transparent',
    padding: 1
  },
  avatarOptionRingActive: {
    borderColor: DS.palette['Brand/Maroon']
  }
})
